"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus } from "@/components/ui/Form";
import { useT } from "@/lib/i18n/context";
import type { CUserAccountsContent } from "@/lib/api/cms-catalog";
import type { UserAccountExtension } from "@/lib/api/user-account-extensions";
import { fetchUserList, type UserListRow } from "@/lib/api/c-user-accounts-user-list";
import { cUserAccountsTranslations } from "./CUserAccounts.i18n";
import { TabHeader } from "./TabHeader";
import styles from "./ProfileTabUserList.module.css";

const PAGE_SIZE = 20;

/**
 * ProfileTabUserList — grid "Użytkownicy" w panelu konta (`c_user_accounts`).
 *
 * Renderowany gdy `content.config.enable_user_list === true` i user kliknie
 * pozycję "Użytkownicy" w sidebar nav (hash `#users`). Pobiera leniwie listę
 * osób z grup wskazanych w konfiguracji katalogu (server-side resolution po
 * `content.id_catalog`).
 *
 * **Grid:** TanStack Table v8 (headless) — sortowanie po nagłówkach, filtr
 * (global search), paginacja — wszystko client-side na pobranym zbiorze
 * (lista jest ograniczona członkami wybranych grup, więc bounded).
 *
 * **Kolumny:** (1) avatar + imię/nazwisko, (2) email `mailto:`, (3) telefon
 * `tel:`, (4) akcja "Wyślij wiadomość".
 *
 * **Wyślij wiadomość:** dostępne tylko gdy komponent `c_private_messages` jest
 * aktywny (wykrywany z `extensions` — przekazane z ProfileTabs, bez nowego
 * fetchu). Link prowadzi do compose formularza z prefill loginu odbiorcy
 * (hash extras `,to=<login>` — parsowane przez NewMessageForm). Gdy komponent
 * niedostępny — tekst "niedostępne" + przycisk disabled.
 */
export function ProfileTabUserList({
  content,
  extensions,
}: {
  content: CUserAccountsContent;
  extensions: UserAccountExtension[];
}) {
  const t = useT(cUserAccountsTranslations);
  const tl = t.userList;

  const [rows, setRows] = useState<UserListRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "user", desc: false }]);

  /* Dostępność prywatnych wiadomości — bez nowego fetchu, reuse `extensions`
     (ProfileTabs już je pobrał). composeHref = href akcji `new-message`. */
  const pm = extensions.find((e) => e.symbol === "c_private_messages");
  /* Klucz akcji compose w `sharingContentForApi` to `new` (href = `extension-new-message`). */
  const composeHref = pm?.actions["new"]?.href ?? null;
  const pmAvailable = !!pm && !!composeHref;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { users, truncated: tr } = await fetchUserList(content.id_catalog);
        if (cancelled) return;
        setRows(users);
        setTruncated(tr);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [content.id_catalog]);

  const columns = useMemo(() => {
    const col = createColumnHelper<UserListRow>();
    return [
      col.accessor((r) => `${r.surname} ${r.name}`.trim(), {
        id: "user",
        header: () => tl.columnUser,
        cell: (ctx) => {
          const r = ctx.row.original;
          const fullName = `${r.name} ${r.surname}`.trim() || r.login;
          return (
            <div className={styles.userCell}>
              <span className={styles.avatar} aria-hidden="true">
                {r.avatar_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={r.avatar_url} alt="" className={styles.avatarImg} />
                ) : (
                  <span className={styles.avatarInitials}>{getInitials(fullName)}</span>
                )}
              </span>
              <span className={styles.userName}>{fullName}</span>
            </div>
          );
        },
      }),
      col.accessor((r) => r.email ?? "", {
        id: "email",
        header: () => tl.columnEmail,
        cell: (ctx) => {
          const email = ctx.row.original.email;
          return email ? (
            <a className={styles.contactLink} href={`mailto:${email}`}>
              <Icon name="envelope-simple" className={styles.contactIcon} />
              {email}
            </a>
          ) : (
            <span className={styles.muted}>{tl.noEmail}</span>
          );
        },
      }),
      col.accessor((r) => r.telephone ?? "", {
        id: "phone",
        header: () => tl.columnPhone,
        cell: (ctx) => {
          const phone = ctx.row.original.telephone;
          return phone ? (
            <a className={styles.contactLink} href={`tel:${sanitizeTel(phone)}`}>
              <Icon name="phone" className={styles.contactIcon} />
              {phone}
            </a>
          ) : (
            <span className={styles.muted}>{tl.noPhone}</span>
          );
        },
      }),
      col.display({
        id: "action",
        header: () => tl.columnAction,
        cell: (ctx) => {
          const r = ctx.row.original;
          if (pmAvailable && composeHref) {
            /* Prefill odbiorcy przez hash extras `,to=<login>` — NewMessageForm
               czyta to z location.hash. Backend send szuka po loginie. */
            const href = `${composeHref},to=${encodeURIComponent(r.login)}`;
            return (
              <a className={styles.sendButton} href={href}>
                <Icon name="paper-plane-tilt" className={styles.sendIcon} />
                {tl.sendMessage}
              </a>
            );
          }
          /* Tooltip na wrapperze (span) - `disabled` button nie wyzwala
             natywnego title hover w niektórych przeglądarkach. */
          return (
            <span className={styles.sendUnavailable} title={tl.messagesUnavailable}>
              <button
                type="button"
                className={styles.sendButtonDisabled}
                disabled
                aria-disabled="true"
                aria-label={`${tl.sendMessage} (${tl.messagesUnavailable})`}
              >
                <Icon name="paper-plane-tilt" className={styles.sendIcon} />
                {tl.sendMessage}
              </button>
            </span>
          );
        },
      }),
    ];
  }, [tl, pmAvailable, composeHref]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <TabHeader icon="list-bullets" title={tl.title} />

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Icon name="magnifying-glass" className={styles.searchIcon} />
          <input
            type="search"
            className={styles.search}
            placeholder={tl.searchPlaceholder}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label={tl.searchPlaceholder}
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">{tl.loading}</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">{tl.empty}</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={
                                  sorted ? styles.sortIconActive : styles.sortIcon
                                }
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && <p className={styles.truncatedHint}>{tl.truncatedHint}</p>}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                {tl.paginationPrev}
              </button>
              <span className={styles.pageInfo}>
                {tl.paginationInfo
                  .replace("{page}", String(pageIndex + 1))
                  .replace("{pages}", String(pageCount))}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {tl.paginationNext}
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/** "Jan Kowalski" -> "JK"; "jankowal" -> "J"; fallback "?". */
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? "?").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}

/** Czyści numer telefonu do formatu bezpiecznego dla `tel:` (cyfry, +, spacje). */
function sanitizeTel(phone: string): string {
  return phone.replace(/[^\d+]/g, "");
}
